
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: 'Purpose is built from:',
    options: [
      'Occasional inspiration',
      'Daily choices aligned with values',
      'Random events',
      'External rewards',
      'Luck'
    ],
    correctAnswer: 1,
    explanation: 'Purpose is built from daily choices aligned with values.'
  },
  {
    question: 'What is personal growth?',
    options: [
      'Staying exactly the same throughout life',
      'Continuous development of skills, mindset, and character',
      'Only learning new job skills',
      'Following others without self-reflection'
    ],
    correctAnswer: 1,
    explanation: 'Personal growth involves the ongoing development of skills, expanding your mindset, strengthening character, and becoming the best version of yourself through intentional self-improvement.'
  },
  {
    question: 'How does resilience contribute to personal fulfillment?',
    options: [
      'Resilience prevents any challenges',
      'Resilience helps navigate setbacks and maintain progress toward meaningful goals',
      'Resilience means avoiding all difficulties',
      'Resilience only helps in professional life'
    ],
    correctAnswer: 1,
    explanation: 'Resilience contributes to fulfillment by helping individuals bounce back from setbacks, learn from challenges, and continue moving toward their meaningful goals despite obstacles.'
  },
  {
    question: 'What role do habits play in personal development?',
    options: [
      'Habits have no impact on growth',
      'Positive habits create the foundation for consistent progress and lasting change',
      'Only bad habits affect development',
      'Habits are impossible to change'
    ],
    correctAnswer: 1,
    explanation: 'Habits are the foundation of personal development - small, consistent positive habits compound over time to create significant growth and lasting positive changes in character and capabilities.'
  },
  {
    question: 'How can someone identify their core personal values?',
    options: [
      'By following popular trends',
      'Through self-reflection on what matters most and observing what energizes or drains them',
      'By copying others\' values',
      'By avoiding deep thinking'
    ],
    correctAnswer: 1,
    explanation: 'Core personal values are identified through honest self-reflection, considering what truly matters to you, observing what activities energize you versus drain you, and examining past decisions for patterns.'
  }
];

const LegendarialModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('legendarialM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Purpose and meaning in life</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Resilience and fulfillment strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Consistency in purposeful habits</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by acting on your purpose and values!</p>
            <p>Ready to deepen your understanding of meaning and fulfillment?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/legendarial-resources')}
          >
            Return to Legendarial Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left legendarial-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Personal Growth and Development</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.<br/>
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuizQuestion.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuizQuestion.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuizQuestion.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuizQuestion.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/legendarial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegendarialModule2QuizPage;


